import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LineChart, Radio, X } from 'lucide-react';
import { NavItem } from '../../constants/nav';
import { SIDEBAR } from '../../constants/testIds';
import { toast } from 'sonner';
import { useAuth } from '../../features/auth/AuthContext';
import { fetchPublicSettings } from '../../features/settings/api/settings';
import type { SiteSettings } from '../../types';

export function Sidebar({ items, open = false, onClose }: { items: NavItem[]; open?: boolean; onClose?: () => void }) {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SiteSettings | null>(null);

  useEffect(() => {
    fetchPublicSettings()
      .then((s) => setSettings(s))
      .catch(() => {});
  }, []);

  const isMemberArea = items.some((i) => i.path === '/member/dashboard');
  const isMentor = !!user && user.roles.includes('mentor');
  const navItems: NavItem[] =
    isMemberArea && isMentor
      ? [...items, { key: 'mentor-live', label: 'Kelola Live Trade', icon: Radio, path: '/mentor/live-trades' }]
      : items;
  return (
    <aside
      className={`w-64 h-screen fixed left-0 top-0 bg-[#050914] border-r border-white/10 flex flex-col z-50 transform transition-transform duration-300 lg:translate-x-0 ${
        open ? 'translate-x-0' : '-translate-x-full'
      }`}
      data-testid={SIDEBAR.container}
    >
      <div className="flex items-center gap-3 px-6 h-20 border-b border-white/10" data-testid={SIDEBAR.brand}>
        <div className="w-9 h-9 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center shadow-[0_0_15px_rgba(0,229,255,0.25)] overflow-hidden shrink-0">
          {settings?.logo_url ? (
            <img src={settings.logo_url} alt="logo" className="w-full h-full object-contain" data-testid="sidebar-logo-img" />
          ) : (
            <LineChart className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
          )}
        </div>
        <div className="leading-tight min-w-0 flex-1">
          <p className="text-white font-medium tracking-tight truncate" title={settings?.site_name || 'Cuantrader'}>{settings?.site_name || 'Cuantrader'}</p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-400/80">Community</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="sidebar-close-button"
          className="lg:hidden text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors shrink-0"
          aria-label="Tutup menu"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          if (item.disabled || !item.path) {
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => toast.info('Segera hadir / Coming soon', { description: item.label })}
                data-testid={`${SIDEBAR.link}-${item.key}`}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/40 hover:text-white/70 hover:bg-white/5 transition-colors text-left"
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                <span className="text-sm">{item.label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={item.key}
              to={item.path}
              data-testid={`${SIDEBAR.link}-${item.key}`}
              className={({ isActive }) =>
                isActive
                  ? 'flex items-center gap-3 px-4 py-3 rounded-xl text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 font-medium'
                  : 'flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:text-white hover:bg-white/5 transition-colors'
              }
            >
              <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="text-sm">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
